
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useRegistration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const registerUser = async ({
    email,
    password,
    fullName,
    companyName,
    selectedBusinessType,
    selectedCurrency,
  }: {
    email: string;
    password: string;
    fullName: string;
    companyName: string;
    selectedBusinessType: string;
    selectedCurrency: string;
  }) => {
    setIsLoading(true);

    try {
      // Validation des champs obligatoires
      if (!email || !password || !fullName || !companyName || !selectedBusinessType) {
        throw new Error("Veuillez remplir tous les champs obligatoires");
      }

      console.log("Tentative d'inscription avec:", {
        email,
        fullName,
        companyName,
        selectedBusinessType,
        selectedCurrency
      });

      // Vérification du type d'entreprise
      const { data: businessType, error: businessTypeError } = await supabase
        .from('business_types')
        .select()
        .eq('id', selectedBusinessType)
        .maybeSingle();

      if (businessTypeError || !businessType) {
        console.error("Erreur lors de la vérification du type d'entreprise:", businessTypeError);
        throw new Error("Type d'entreprise non trouvé");
      }

      // Construction des métadonnées utilisateur
      const metadata = {
        user_type: "owner",
        login_type: "owner",
        full_name: fullName,
        company_name: companyName,
        business_type_id: selectedBusinessType,
        currency: selectedCurrency || "FCFA",
      };

      console.log("Inscription avec les métadonnées:", metadata);

      // Créer l'utilisateur avec Supabase
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (authError) {
        console.error("Erreur d'authentification:", authError);
        if (authError.message.includes('User already registered')) {
          throw new Error("Un compte existe déjà avec cet email");
        }
        throw authError;
      }

      if (!authData.user) {
        throw new Error("Aucune donnée utilisateur retournée après l'inscription");
      }

      console.log("Inscription réussie:", authData);

      toast.success(
        "Compte créé avec succès! Veuillez vérifier votre email pour confirmer votre compte."
      );

      // Redirection vers l'interface appropriée après un délai
      setTimeout(() => {
        navigate('/login-owner');
      }, 2000);

    } catch (error: any) {
      console.error("Erreur détaillée lors de l'inscription:", error);
      
      let errorMessage = "Une erreur est survenue lors de la création du compte. Veuillez réessayer.";
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`Erreur lors de l'inscription: ${errorMessage}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { registerUser, isLoading };
};
